/**
 * useWebSocketHandlers Hook
 * Phase 1 Day 6 - WebSocket message handling
 *
 * Manages the WebSocket message handler reference.
 * The actual registration with the WebSocket store happens in AppContent
 * to avoid subscription/re-render issues.
 *
 * @returns {Object} WebSocket handler management functions
 */

import { useCallback, useRef } from "react";
import logger from "../utils/logger";

export const useWebSocketHandlers = () => {
  // Ref to hold the current message handler
  const handleWebSocketMessageRef = useRef(null);

  /**
   * Set the WebSocket message handler reference
   * This just updates the ref - registration happens externally
   * @param {Function} handler - The message handler function
   */
  const setHandler = useCallback((handler) => {
    if (!handler || typeof handler !== "function") {
      logger.log("[useWebSocketHandlers] ⚠️ Invalid handler provided");
      return;
    }

    handleWebSocketMessageRef.current = handler;
    logger.log("[useWebSocketHandlers] ✅ Handler reference set");
  }, []);

  /**
   * Clear the WebSocket message handler
   */
  const clearHandler = useCallback(() => {
    handleWebSocketMessageRef.current = null;
    logger.log("[useWebSocketHandlers] ✅ Handler reference cleared");
  }, []);

  /**
   * Get the current message handler ref
   * Useful for accessing the handler in other hooks/components
   */
  const getHandlerRef = useCallback(() => {
    return handleWebSocketMessageRef;
  }, []);

  /**
   * Check if a handler is currently registered
   */
  const hasHandler = useCallback(() => {
    return handleWebSocketMessageRef.current !== null;
  }, []);

  return {
    // Ref for external access
    handleWebSocketMessageRef,

    // Actions
    setHandler,
    clearHandler,

    // Utilities
    getHandlerRef,
    hasHandler,
  };
};
